import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Table } from 'govuk-react-jsx';
import { Link, useParams } from 'react-router-dom';
import { getApplicationClaim } from '../../firebase';
import downloadFiles from '../../utils/gcp-storage';
import { getDocumentId } from '../../features/dashboard/dashboardSlice';
import {
  getDashboardLink,
  getCurrentLanguage,
  getCopyProviderCY,
  getCopyProviderEN,
} from '../../features/app/appSlice';
import {
  formatDateLocale,
  capitalizeFirstLetter,
  buildingTypeDisplay,
} from '../../utils/helpers';

const ApplicationDetailsTable = ({ status, sendDate, sendCompanyName }) => {
  const { id } = useParams();
  const dashboardLink = useSelector(getDashboardLink);
  const documentId = useSelector(getDocumentId);
  const currentLanguage = useSelector(getCurrentLanguage);
  const copyProviderCY = useSelector(getCopyProviderCY);
  const copyProviderEN = useSelector(getCopyProviderEN);
  const copyProvider =
    currentLanguage === 'en' ? copyProviderEN : copyProviderCY;

  const [applicationClaim, setApplicationClaim] = useState({
    invoice_evidence: [],
  });
  const [claimStatusDate, setClaimStatusDate] = useState('');
  const [appSubmittedDateByLandlord, setAppSubmittedDateByLandlord] =
    useState('');
  const [claimSubmittedDateByInstaller, setClaimSubmittedByInstaller] =
    useState('');
  // NB: used to determine who completed installation (Subcontractor or Employee)
  const [subcontractorId, setSubcontractorId] = useState('');

  const [claimInstallationCompletedDate, setClaimInstallationCompletedDate] =
    useState('');
  const [whoCompletedTheInstallation, setwhoCompletedTheInstallation] =
    useState('');
  const [grantTypeShort, setGrantTypeShort] = useState('');
  const [grantType, setGrantType] = useState('');
  const [displayForLandlord, setDisplayForLandlord] = useState(false);
  const [
    displayApprovedDeclinedCompleted,
    setDisplayApprovedDeclinedCompleted,
  ] = useState(false);

  useEffect(() => {
    // Check if required values are present
    let docuId = documentId;
    if (id !== undefined) docuId = id;

    const fetchData = async () => {
      try {
        const response = await getApplicationClaim({ id: docuId });
        const { data } = response;
        setGrantTypeShort(capitalizeFirstLetter(data.grant_type_display));
        setGrantType(data.grant_type);
        const claimStDt = new Date(
          data.claim_status_updated_ts !== undefined
            ? // eslint-disable-next-line no-underscore-dangle
              data.claim_status_updated_ts._seconds * 1000
            : null
        );
        setClaimStatusDate(
          data.claim_status_updated_ts !== undefined
            ? formatDateLocale(claimStDt)
            : ''
        );
        const appSubOn = new Date(
          data.application_submitted_date !== undefined
            ? // eslint-disable-next-line no-underscore-dangle
              data.application_submitted_date._seconds * 1000
            : null
        );
        setAppSubmittedDateByLandlord(
          data.application_submitted_date !== undefined
            ? formatDateLocale(appSubOn)
            : ''
        );
        const installerSubOn = new Date(
          data.claim_submitted_date !== undefined
            ? // eslint-disable-next-line no-underscore-dangle
              data.claim_submitted_date._seconds * 1000
            : null
        );
        setClaimSubmittedByInstaller(
          data.claim_submitted_date !== undefined
            ? formatDateLocale(installerSubOn)
            : ''
        );
        const claimInstallationCompletedDt = new Date(
          data.installation_completed_date !== undefined
            ? // eslint-disable-next-line no-underscore-dangle
              data.installation_completed_date._seconds * 1000
            : null
        );
        setClaimInstallationCompletedDate(
          data.installation_completed_date !== undefined
            ? formatDateLocale(claimInstallationCompletedDt)
            : ''
        );
        setwhoCompletedTheInstallation(
          data.subcontractor_company_name !== undefined
            ? data.subcontractor_company_name
            : ''
        );
        const claimSubmissionDt = new Date(
          data.claim_submitted_date !== undefined
            ? // eslint-disable-next-line no-underscore-dangle
              data.claim_submitted_date._seconds * 1000
            : null
        );
        sendDate(
          data.claim_submitted_date === undefined
            ? formatDateLocale(claimStDt)
            : formatDateLocale(claimSubmissionDt)
        );
        setSubcontractorId(data.subcontractor_id);
        sendCompanyName(data.landlord_account_name);
        if (
          status === 'Approved for Payment' ||
          status === 'Declined by Installer' ||
          status === 'Completet by Installer'
        ) {
          setDisplayApprovedDeclinedCompleted(true);
        }
        if (
          status !== 'Approved for Payment' &&
          status !== 'Declined by Installer' &&
          status !== 'Submitted for Review' &&
          status !== 'Completet by Installer'
        ) {
          setDisplayForLandlord(true);
        }

        setApplicationClaim(data);
      } catch (error) {
        console.error('Error fetching claim:', error);
        if (error.code === 'functions/permission-denied' && dashboardLink) {
          console.log('Redirecting to:', dashboardLink);
          window.location.assign(dashboardLink);
        }
      }
    };
    fetchData();
  }, [dashboardLink, id]);

  let dateOfClaim;
  if (status === 'Completed' || status === 'Completet by Installer') {
    dateOfClaim = [
      {
        children: copyProvider.getCopy(
          'dashboardHub.ApplicationDetailsTable.s10',
          'Date the claim was approved'
        ),
        format: 'text',
      },
      {
        children: claimStatusDate,
        format: 'text',
      },
    ];
  } else if (status === 'Approved for Payment') {
    dateOfClaim = [
      {
        children: copyProvider.getCopy(
          'dashboardHub.ApplicationDetailsTable.s10',
          'Date the claim was approved'
        ),
        format: 'text',
      },
      {
        children: claimStatusDate,
        format: 'text',
      },
    ];
  } else if (status === 'Declined' || status === 'Declined by Installer') {
    dateOfClaim = [
      {
        children: copyProvider.getCopy(
          'dashboardHub.ApplicationDetailsTable.s30',
          'Date the claim was declined'
        ),
        format: 'text',
      },
      {
        children: claimStatusDate,
        format: 'text',
      },
    ];
  } else if (status === 'Submitted by Installer') {
    dateOfClaim = [
      {
        children: copyProvider.getCopy(
          'dashboardHub.ApplicationDetailsTable.s31',
          'Date the claim was submitted'
        ),
        format: 'text',
      },
      {
        children: appSubmittedDateByLandlord, // lan
        format: 'text',
      },
    ];
  } else if (status === 'Awaiting customer review') {
    dateOfClaim = [
      {
        children: copyProvider.getCopy(
          'dashboardHub.ApplicationDetailsTable.s31',
          'Date the claim was submitted'
        ),
        format: 'text',
      },
      {
        children: appSubmittedDateByLandlord, // lan
        format: 'text',
      },
    ];
  } else if (status === 'Submitted for Review') {
    dateOfClaim = [
      {
        children: copyProvider.getCopy(
          'dashboardHub.ApplicationDetailsTable.s31a',
          'Date you submitted this claim'
        ),
        format: 'text',
      },
      {
        children: claimSubmittedDateByInstaller,

        format: 'text',
      },
    ];
  } else {
    dateOfClaim = [];
  }

  let dateOfInstallation;
  if (
    status === 'Submitted by Installer' ||
    status === 'Declined' ||
    status === 'Completed' ||
    status === 'Awaiting customer review'
  ) {
    dateOfInstallation = [
      {
        children: copyProvider.getCopy(
          'dashboardHub.ApplicationDetailsTable.s32',
          'Date of installation'
        ),
        format: 'text',
      },
      {
        children: claimInstallationCompletedDate,
        format: 'text',
      },
    ];
  } else {
    dateOfInstallation = [];
  }

  let dtOfInstallation;
  if (displayForLandlord === false) {
    dtOfInstallation = [
      {
        children:
          // eslint-disable-next-line no-nested-ternary
          status === 'Declined by Installer'
            ? copyProvider.getCopy(
                'dashboardHub.ApplicationDetailsTable.s32',
                'Date of installation'
              )
            : grantType === 'LANDLORD_RESIDENTIAL_GRANT'
            ? copyProvider.getCopy(
                'dashboardHub.ApplicationDetailsTable.s32a',
                'Date installations were completed'
              )
            : copyProvider.getCopy(
                'dashboardHub.ApplicationDetailsTable.s32',
                'Date of installation'
              ),
        format: 'text',
      },
      {
        children: claimInstallationCompletedDate,
        format: 'text',
      },
    ];
  } else {
    dtOfInstallation = [];
  }
  let whoCompletedInstallation;
  if (status === 'Submitted for Review' || status === 'Approved for Payment') {
    whoCompletedInstallation = [
      {
        children: copyProvider.getCopy(
          'dashboardHub.ApplicationDetailsTable.s32b',
          'Who completed the installation'
        ),
        format: 'text',
      },
      {
        children: (
          <section>
            {subcontractorId === undefined || subcontractorId === '' ? (
              'Your company'
            ) : (
              <p>
                {copyProvider.getCopy(
                  'dashboardHub.ApplicationDetailsTable.s34',
                  'Subcontractor '
                )}
                <br />
                {whoCompletedTheInstallation}
                <br />
                {subcontractorId}
              </p>
            )}
          </section>
        ),
        format: 'text',
      },
    ];
  } else {
    whoCompletedInstallation = [];
  }

  let showStatus;

  if (status === 'Completed' || status === 'Completet by Installer') {
    showStatus = (
      <strong className="govuk-tag govuk-tag--green">Completed</strong>
    );
  } else if (status === 'Approved for Payment') {
    showStatus = (
      <strong className="govuk-tag govuk-tag--green">
        Approved For Payment
      </strong>
    );
  } else if (status === 'Declined' || status === 'Declined by Installer') {
    showStatus = (
      <strong className="govuk-tag govuk-tag--grey">Declined</strong>
    );
  } else if (status === 'Submitted by Installer') {
    showStatus = (
      <strong className="govuk-tag govuk-tag--blue">
        Submitted by Installer
      </strong>
    );
  } else if (status === 'Awaiting customer review') {
    showStatus = (
      <strong className="govuk-tag govuk-tag--red">
        Awaiting customer review
      </strong>
    );
  } else if (status === 'Submitted for Review') {
    showStatus = (
      <strong className="govuk-tag govuk-tag--yellow">
        Submitted for Review
      </strong>
    );
  } else {
    showStatus = '';
  }

  let secondaryHeading;

  switch (status) {
    case 'Declined':
    case 'Completed':
    case 'Submitted by Installer':
    case 'Awaiting customer review':
      secondaryHeading = copyProvider.getCopy(
        'dashboardHub.ApplicationDetailsTable.s11a',
        'Installation details'
      );
      break;
    default:
      secondaryHeading = copyProvider.getCopy(
        'dashboardHub.ApplicationDetailsTable.s11',
        'Address details'
      );
  }

  const invoiceRows = applicationClaim.invoice_evidence
    ? applicationClaim.invoice_evidence.map((invoiceEvidence) => {
        return {
          cells: [
            {
              children:
                status === 'Declined' ||
                status === 'Completed' ||
                status === 'Submitted by Installer' ||
                status === 'Awaiting customer review'
                  ? copyProvider.getCopy(
                      'dashboardHub.ApplicationDetailsTable.s19a',
                      'Proof of customer invoice'
                    )
                  : copyProvider.getCopy(
                      'dashboardHub.ApplicationDetailsTable.s19',
                      "Customer's invoice"
                    ),
              format: 'text',
            },
            {
              children: invoiceEvidence.invoice_link,
              format: 'text',
            },
            {
              children: (
                <Link
                  className="govuk-link"
                  to={invoiceEvidence.invoice_link}
                  onClick={(e) => {
                    e.preventDefault();
                    downloadFiles([invoiceEvidence.invoice_link]);
                  }}
                  target="_blank"
                  download
                >
                  {copyProvider.getCopy(
                    'dashboardHub.ApplicationDetailsTable.s21',
                    'Download'
                  )}
                  <br />
                  {copyProvider.getCopy(
                    'dashboardHub.ApplicationDetailsTable.s22',
                    'invoice'
                  )}
                </Link>
              ),
              format: 'numeric',
            },
          ],
        };
      })
    : [];

  const chargepointRows = applicationClaim.chargepoints_details
    ? applicationClaim.chargepoints_details.map((chargepointDetails, index) => {
        return {
          cells: [
            {
              children: (
                <section>
                  {copyProvider.getCopy(
                    'dashboardHub.ApplicationDetailsTable.s33',
                    'Chargepoint '
                  )}
                  {index + 1}
                </section>
              ),
              format: 'text',
            },
            {
              children: (
                <table>
                  <tr>
                    <th>
                      <section>
                        <strong>
                          {copyProvider.getCopy(
                            'dashboardHub.ApplicationDetailsTable.s28',
                            'Manufacturer: '
                          )}
                        </strong>
                      </section>
                    </th>
                  </tr>
                  <tr>
                    <td>
                      {chargepointDetails.chargepoint_manufacturer &&
                        chargepointDetails.chargepoint_manufacturer}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <section>
                        <strong>
                          {copyProvider.getCopy(
                            'dashboardHub.ApplicationDetailsTable.s27',
                            'Model: '
                          )}
                        </strong>
                      </section>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      {chargepointDetails.chargepoint_model &&
                        chargepointDetails.chargepoint_model}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <section>
                        <strong>
                          {copyProvider.getCopy(
                            'dashboardHub.ApplicationDetailsTable.s0',
                            'Chargepoint sockets: '
                          )}
                        </strong>
                      </section>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      {chargepointDetails.number_of_sockets &&
                        chargepointDetails.number_of_sockets}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <section>
                        <strong>
                          {copyProvider.getCopy(
                            'dashboardHub.ApplicationDetailsTable.s26',
                            'Serial number: '
                          )}
                        </strong>
                      </section>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      {chargepointDetails.serial_number &&
                        chargepointDetails.serial_number}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <section>
                        <strong>
                          {copyProvider.getCopy(
                            'dashboardHub.ApplicationDetailsTable.s25',
                            'Photo:'
                          )}
                        </strong>
                      </section>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <ul>
                        {chargepointDetails.evidence.map((photo, i) => (
                          <li key={i.id} style={{ listStyle: 'none' }}>
                            {photo.image}
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                </table>

                // </>
              ),
              format: 'text',
            },
            {
              children: (
                <Link
                  className="govuk-link"
                  to="/"
                  onClick={(e) => {
                    e.preventDefault();
                    downloadFiles(
                      chargepointDetails.evidence.map(
                        (evidence) => evidence.image
                      ),
                      chargepointDetails.serial_number.concat('.zip')
                    );
                  }}
                  target="_blank"
                  download
                >
                  {copyProvider.getCopy(
                    'dashboardHub.ApplicationDetailsTable.s21',
                    'Download '
                  )}
                  <br />
                  {copyProvider.getCopy(
                    'dashboardHub.ApplicationDetailsTable.s24',
                    'photos '
                  )}
                  <br />
                </Link>
              ),
              format: 'numeric',
            },
          ],
        };
      })
    : [];

  const pageModel = (
    <>
      <Table
        firstCellIsHeader
        rows={[
          {
            cells: [
              {
                children: copyProvider.getCopy(
                  'dashboardHub.ApplicationDetailsTable.s4',
                  'Grant type'
                ),
                format: 'text',
                className: 'govuk-summary-list__key',
              },
              {
                children: grantTypeShort,
                format: 'text',
              },
            ],
          },
          {
            cells: [
              {
                children: copyProvider.getCopy(
                  'dashboardHub.ApplicationDetailsTable.s5',
                  'Reference number'
                ),
                format: 'text',
              },
              {
                children: applicationClaim.claim_id,
                format: 'text',
              },
            ],
          },
          {
            cells: [
              {
                children:
                  displayForLandlord === false
                    ? copyProvider.getCopy(
                        'dashboardHub.ApplicationDetailsTable.s6',
                        "Customer's name"
                      )
                    : copyProvider.getCopy(
                        'dashboardHub.ApplicationDetailsTable.s6a',
                        'Company name'
                      ),
                format: 'text',
              },
              {
                children: applicationClaim.landlord_account_name,
                format: 'text',
              },
            ],
          },
          {
            cells: [
              {
                children:
                  // eslint-disable-next-line no-nested-ternary
                  displayForLandlord === false
                    ? grantType === 'LANDLORD_RESIDENTIAL_GRANT'
                      ? copyProvider.getCopy(
                          'dashboardHub.ApplicationDetailsTable.s9',
                          'Claim status'
                        )
                      : copyProvider.getCopy(
                          'dashboardHub.ApplicationDetailsTable.s9a',
                          'Status of claim'
                        )
                    : copyProvider.getCopy(
                        'dashboardHub.ApplicationDetailsTable.s9a',
                        'Status of claim'
                      ),
                format: 'text',
              },
              {
                children: showStatus,
                format: 'text',
              },
            ],
          },
          displayForLandlord === true
            ? {
                cells: [
                  {
                    children: copyProvider.getCopy(
                      'dashboardHub.ApplicationDetailsTable.s7',
                      'Installer name'
                    ),
                    format: 'text',
                  },
                  {
                    children: applicationClaim.installer_company_name,
                    format: 'text',
                  },
                ],
              }
            : {},
          displayApprovedDeclinedCompleted === true
            ? {
                cells: [
                  {
                    children: copyProvider.getCopy(
                      'dashboardHub.ApplicationDetailsTable.s35',
                      'Date you submitted the claim'
                    ),
                    format: 'text',
                  },
                  {
                    children: appSubmittedDateByLandlord,
                    format: 'text',
                  },
                ],
              }
            : {},
          displayForLandlord === true
            ? {
                cells: [
                  {
                    children: copyProvider.getCopy(
                      'dashboardHub.ApplicationDetailsTable.s8',
                      "Installer's OZEV number"
                    ),
                    format: 'text',
                  },
                  {
                    children: applicationClaim.installer_id,
                    format: 'text',
                  },
                ],
              }
            : {},
          {
            cells: dateOfClaim,
          },
        ]}
      />
      <h2 className="govuk-heading-m govuk-!-margin-top-7">
        {secondaryHeading}
      </h2>
      <Table
        firstCellIsHeader
        rows={[
          displayForLandlord === true
            ? {
                cells: [
                  {
                    children: copyProvider.getCopy(
                      'dashboardHub.ApplicationDetailsTable.s12',
                      'Private parking'
                    ),
                    format: 'text',
                    className: 'govuk-summary-list__key',
                  },
                  {
                    children: applicationClaim.is_private_parking
                      ? 'Yes'
                      : 'No',
                    format: 'text',
                  },
                ],
              }
            : {},
          displayForLandlord === true &&
          grantTypeShort !== 'Commercial properties'
            ? {
                cells: [
                  {
                    children: copyProvider.getCopy(
                      'dashboardHub.ApplicationDetailsTable.s13',
                      'Property type'
                    ),
                    format: 'text',
                  },
                  {
                    children: buildingTypeDisplay(
                      applicationClaim.building_type
                    ),
                    format: 'text',
                  },
                ],
              }
            : {},
          {
            cells: [
              {
                children: copyProvider.getCopy(
                  'dashboardHub.ApplicationDetailsTable.s14',
                  'Address'
                ),
                format: 'text',
              },
              {
                children: (
                  <>
                    <section>
                      {applicationClaim.installation_address_line_1 &&
                        `${applicationClaim.installation_address_line_1}`}
                    </section>
                    <section>
                      {applicationClaim.installation_address_line_2 &&
                        `${applicationClaim.installation_address_line_2}`}
                    </section>
                    <section>
                      {applicationClaim.installation_address_city &&
                        `${applicationClaim.installation_address_city}`}
                    </section>
                    <section>
                      {applicationClaim.installation_address_county &&
                        `${applicationClaim.installation_address_county}`}
                    </section>
                    <section>
                      {applicationClaim.installation_address_postcode &&
                        `${applicationClaim.installation_address_postcode}`}
                    </section>
                  </>
                ),
                format: 'text',
              },
            ],
          },
          {
            cells: dateOfInstallation,
          },
          {
            cells: whoCompletedInstallation,
          },
          {
            cells: dtOfInstallation,
          },
          {
            cells: [
              {
                children: copyProvider.getCopy(
                  'dashboardHub.ApplicationDetailsTable.s15',
                  'Number of chargepoints'
                ),
                format: 'text',
              },
              {
                // NB: this value is set based on the number of chargepoints in the mock data.
                children: applicationClaim.number_of_chargepoints_applied,
                format: 'text',
              },
            ],
          },
          {
            cells: [
              {
                children: copyProvider.getCopy(
                  'dashboardHub.ApplicationDetailsTable.s16',
                  'Number of chargepoint sockets'
                ),
                format: 'text',
              },
              {
                children:
                  applicationClaim.number_of_chargepoint_sockets_applied,
                format: 'text',
              },
            ],
          },
        ]}
      />
      <h1 className="govuk-heading-m govuk-!-margin-top-7">
        {copyProvider.getCopy(
          'dashboardHub.ApplicationDetailsTable.s17',
          'Financial details'
        )}
      </h1>
      {/* Invoice Details */}
      <Table
        firstCellIsHeader
        rows={[
          {
            cells: [
              {
                children: copyProvider.getCopy(
                  'dashboardHub.ApplicationDetailsTable.s18',
                  'Total cost of installation'
                ),
                format: 'text',
                className: 'govuk-summary-list__key',
              },
              {
                children: applicationClaim.total_installation_cost
                  ? `£${applicationClaim.total_installation_cost}`
                  : '0.00',
                format: 'text',
              },
              {
                children: '',
                format: 'text',
              },
            ],
          },
        ]
          .concat(invoiceRows)
          .concat({
            cells: [
              {
                children: copyProvider.getCopy(
                  'dashboardHub.ApplicationDetailsTable.s20',
                  'Total grant amount'
                ),
                format: 'text',
              },
              {
                children: applicationClaim.total_grant_amount
                  ? `£${Number(applicationClaim.total_grant_amount).toFixed(2)}`
                  : '0.00',
                format: 'text',
              },
              {
                children: '',
                format: 'text',
              },
            ],
          })}
      />
      <h1 className="govuk-heading-m govuk-!-margin-top-7">
        {copyProvider.getCopy(
          'dashboardHub.ApplicationDetailsTable.s23',
          'Chargepoint details'
        )}
      </h1>
      {/* Chargepoint Details */}
      {applicationClaim.chargepoints_details && (
        <Table firstCellIsHeader rows={chargepointRows} />
      )}
    </>
  );
  return pageModel;
};

ApplicationDetailsTable.propTypes = {
  // eslint-disable-next-line react/no-unused-prop-types
  sendDate: PropTypes.node,
  // eslint-disable-next-line react/no-unused-prop-types
  sendCompanyName: PropTypes.node,
  status: PropTypes.string,
};

ApplicationDetailsTable.defaultProps = {
  sendDate: '',
  sendCompanyName: '',
  status: '',
};

export default ApplicationDetailsTable;
